/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  KnowledgeBaseQaPairCategory,
  KnowledgeBaseQaPairStatus,
} from 'uplisting-frontend/models/ai-knowledge-base-qa-pair';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';

interface IArgs {
  qaPair?: ModelRegistry['ai-knowledge-base-qa-pair'];
  properties: ModelRegistry['property'][];
  onClose(didSave?: boolean): void;
  onViewSource?(qaPair: ModelRegistry['ai-knowledge-base-qa-pair']): void;
}

interface QaPairPanelSignature {
  Element: HTMLDivElement;
  Args: IArgs;
}

export default class UiKnowledgeCenterQaPairPanelComponent extends Component<QaPairPanelSignature> {
  @service intl!: Services['intl'];
  @service notifications!: Services['notifications'];
  @service sentry!: Services['sentry'];
  @service('repositories/ai-knowledge-base-qa-pair')
  qaPairRepository!: Services['repositories/ai-knowledge-base-qa-pair'];

  changeset: GenericChangeset<ModelRegistry['ai-knowledge-base-qa-pair']>;

  constructor(owner: Owner, args: IArgs) {
    super(owner, args);

    this.changeset = this.qaPairRepository.buildChangeset(args.qaPair);
  }

  @cached
  get isEditMode(): boolean {
    return !!this.args.qaPair && !this.args.qaPair.isNew;
  }

  @cached
  get title(): string {
    return this.isEditMode
      ? this.intl.t('knowledge_center.panel.title_edit')
      : this.intl.t('knowledge_center.panel.title_add');
  }

  @cached
  get propertyName(): string | undefined {
    return this.args.qaPair?.property?.nickname;
  }

  categoryOptions = Object.values(KnowledgeBaseQaPairCategory);
  statusOptions = Object.values(KnowledgeBaseQaPairStatus);

  @cached
  get propertyOptions(): ModelRegistry['property'][] {
    return this.args.properties;
  }

  get characterCount(): number {
    return (this.changeset.answer || '').length;
  }

  get confidencePercent(): number | undefined {
    const score = this.args.qaPair?.confidenceScore;

    if (score === undefined || score === null) return undefined;

    return Math.round(score * 100);
  }

  @action
  @waitFor
  async handleSave(): Promise<void> {
    await this.changeset.validate();

    if (this.changeset.isInvalid) {
      return;
    }

    try {
      await this.changeset.save();

      this.notifications.info('knowledge_center.notifications.saved');
      this.args.onClose(true);
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  @action
  handleViewSource(): void {
    if (this.args.qaPair && this.args.onViewSource) {
      this.args.onViewSource(this.args.qaPair);
    }
  }

  @action
  handleClose(): void {
    if (this.isEditMode) {
      this.changeset.rollback();
    }

    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::KnowledgeCenter::QaPairPanel': typeof UiKnowledgeCenterQaPairPanelComponent;
  }
}
